import type { CSSProperties } from 'react';
import React, { Component } from 'react';

import { makeRGB, N0, N10, N800 } from '@trello/colors';
import type { EmojiMartEmoji } from '@trello/emoji';
import { currentLocale } from '@trello/locale';
import { dangerouslyGetComputedToken } from '@trello/theme';

import { EmojiProviderComponent } from './EmojiProviderComponent';
import { LazyEmojiMartPicker } from './LazyEmojiMartPicker';

import styles from './EmojiPicker.less';

export interface EmojiPickerProps {
  autoFocus?: boolean;
  dynamicWidth?: boolean;
  emojiButtonRadius?: string;
  emojiButtonSize?: number;
  emojiSize?: number;
  getSpritesheetURL?: (set: 'twitter', sheetSize?: 32 | 20 | 16 | 64) => string;
  icons?: 'auto' | 'outline' | 'solid';
  locale?: string;
  maxFrequentRows?: number;
  onEmojiSelect?(emoji: EmojiMartEmoji, e: React.MouseEvent): void;
  set?: string;
  theme?: 'dark' | 'light';
}

export class EmojiPicker extends Component<EmojiPickerProps> {
  static defaultProps: EmojiPickerProps = {
    autoFocus: true,
    dynamicWidth: false,
    emojiButtonRadius: '3px',
    emojiButtonSize: 42,
    emojiSize: 28,
    maxFrequentRows: 2,
    icons: 'outline',
    set: 'twitter',
    theme: 'light', // theme is overridden by styles.emojiPicker
  };

  render() {
    const hexColors = {
      background: dangerouslyGetComputedToken('elevation.surface.overlay', N10),
      text: dangerouslyGetComputedToken('color.text', N800),
      input: dangerouslyGetComputedToken('color.background.input', N0),
    };

    // emoji-mart custom styles only support rgb values
    // other styling is handled in EmojiPicker.less
    const emojiMartCustomStyles = {
      '--rgb-background': makeRGB(hexColors.background),
      '--rgb-color': makeRGB(hexColors.text),
      '--rgb-input': makeRGB(hexColors.input),
    } as CSSProperties;

    return (
      <div className={styles.emojiPicker} style={emojiMartCustomStyles}>
        <EmojiProviderComponent>
          {(sheet, backgroundImageFn) => {
            // if sheet is defined, we've loaded emoji-mart
            if (sheet) {
              return (
                <LazyEmojiMartPicker
                  {...this.props}
                  locale={currentLocale.slice(0, 2)} // emoji-mart locales are only two characters
                  getSpritesheetURL={backgroundImageFn}
                />
              );
            } else {
              <></>;
            }
          }}
        </EmojiProviderComponent>
      </div>
    );
  }
}
